// Future-facing PromoShowcase component registry.
//
// This registry is intentionally not wired into current layout_schema persistence
// or WordPress publishing yet. It establishes one canonical definition for the
// next builder document model so preview, WordPress, native rendering, Inspector
// controls, and future AI tooling can share the same component contract.

export const COMPONENT_KINDS = Object.freeze({
  PRIMITIVE: 'primitive',
  COMPOSED: 'composed',
  COMMERCE: 'commerce',
  STRUCTURAL: 'structural',
  CUSTOM: 'custom',
});

export const COMPONENT_CATEGORIES = Object.freeze({
  LAYOUT: 'layout',
  TYPOGRAPHY: 'typography',
  MEDIA: 'media',
  ACTIONS: 'actions',
  CONTENT: 'content',
  COMMERCE: 'commerce',
  GLOBAL: 'global',
  CUSTOM: 'custom',
});

const createDefinition = (definition) => Object.freeze({
  version: 1,
  acceptsChildren: false,
  defaultProps: {},
  defaultStyle: {},
  propSchema: {},
  styleSchema: {},
  responsiveSchema: {},
  capabilities: [],
  selectors: {},
  ...definition,
});

const commonSurfaceStyle = {
  background: { type: 'color-token-or-value' },
  color: { type: 'color-token-or-value' },
  borderColor: { type: 'color-token-or-value' },
  borderRadius: { type: 'radius-token-or-value' },
  boxShadow: { type: 'shadow-token-or-value' },
};

const commonSpacingStyle = {
  marginBlock: { type: 'spacing-token-or-value' },
  marginInline: { type: 'spacing-token-or-value' },
  paddingBlock: { type: 'spacing-token-or-value' },
  paddingInline: { type: 'spacing-token-or-value' },
};

export const CORE_COMPONENT_REGISTRY = Object.freeze({
  'core/section': createDefinition({
    type: 'core/section',
    name: 'Section',
    description: 'Top-level page section with contained or full-width layout.',
    category: COMPONENT_CATEGORIES.LAYOUT,
    kind: COMPONENT_KINDS.PRIMITIVE,
    acceptsChildren: true,
    defaultProps: { widthMode: 'contained', semanticTag: 'section' },
    defaultStyle: { paddingBlock: 'section-md', background: 'transparent' },
    propSchema: {
      widthMode: { type: 'enum', options: ['contained', 'full'] },
      semanticTag: { type: 'enum', options: ['section', 'div', 'main', 'aside'] },
    },
    styleSchema: {
      ...commonSpacingStyle,
      ...commonSurfaceStyle,
      minHeight: { type: 'length' },
      overflow: { type: 'enum', options: ['visible', 'hidden', 'clip'] },
    },
    responsiveSchema: { paddingBlock: true, paddingInline: true, minHeight: true },
    selectors: { root: '.ps-section', inner: '.ps-section__inner' },
    rendererContract: 'core/section@1',
  }),

  'core/container': createDefinition({
    type: 'core/container',
    name: 'Container',
    description: 'Reusable width-constrained layout container.',
    category: COMPONENT_CATEGORIES.LAYOUT,
    kind: COMPONENT_KINDS.PRIMITIVE,
    acceptsChildren: true,
    defaultProps: { size: 'default', alignment: 'stretch' },
    defaultStyle: { width: '100%', maxWidth: 'container-default', marginInline: 'auto' },
    propSchema: {
      size: { type: 'enum', options: ['narrow', 'default', 'wide', 'full'] },
      alignment: { type: 'enum', options: ['stretch', 'start', 'center', 'end'] },
    },
    styleSchema: {
      ...commonSpacingStyle,
      width: { type: 'length' },
      maxWidth: { type: 'container-token-or-value' },
    },
    responsiveSchema: { width: true, maxWidth: true, paddingInline: true },
    selectors: { root: '.ps-container' },
    rendererContract: 'core/container@1',
  }),

  'core/flex': createDefinition({
    type: 'core/flex',
    name: 'Flex Row',
    description: 'Flexible row/column layout primitive for arbitrary child composition.',
    category: COMPONENT_CATEGORIES.LAYOUT,
    kind: COMPONENT_KINDS.PRIMITIVE,
    acceptsChildren: true,
    defaultProps: { direction: 'row', wrap: 'nowrap', justify: 'start', align: 'stretch', gap: 'space-4' },
    propSchema: {
      direction: { type: 'enum', options: ['row', 'column'] },
      wrap: { type: 'enum', options: ['nowrap', 'wrap'] },
      justify: { type: 'enum', options: ['start', 'center', 'end', 'between', 'around', 'evenly'] },
      align: { type: 'enum', options: ['stretch', 'start', 'center', 'end', 'baseline'] },
      gap: { type: 'spacing-token-or-value' },
    },
    responsiveSchema: { direction: true, wrap: true, justify: true, align: true, gap: true },
    selectors: { root: '.ps-flex' },
    rendererContract: 'core/flex@1',
  }),

  'core/grid': createDefinition({
    type: 'core/grid',
    name: 'Grid',
    description: 'Responsive CSS-grid layout primitive for arbitrary child composition.',
    category: COMPONENT_CATEGORIES.LAYOUT,
    kind: COMPONENT_KINDS.PRIMITIVE,
    acceptsChildren: true,
    defaultProps: { columns: 2, gap: 'space-4', align: 'stretch' },
    propSchema: {
      columns: { type: 'number', min: 1, max: 12 },
      gap: { type: 'spacing-token-or-value' },
      align: { type: 'enum', options: ['stretch', 'start', 'center', 'end'] },
    },
    responsiveSchema: { columns: true, gap: true, align: true },
    selectors: { root: '.ps-grid' },
    rendererContract: 'core/grid@1',
  }),

  'core/heading': createDefinition({
    type: 'core/heading',
    name: 'Heading',
    description: 'Semantic heading with design-token typography controls.',
    category: COMPONENT_CATEGORIES.TYPOGRAPHY,
    kind: COMPONENT_KINDS.PRIMITIVE,
    defaultProps: { text: 'Heading', level: 2 },
    defaultStyle: { typography: 'heading-md', color: 'text-primary' },
    propSchema: {
      text: { type: 'text' },
      level: { type: 'enum', options: [1, 2, 3, 4, 5, 6] },
    },
    styleSchema: {
      ...commonSpacingStyle,
      color: { type: 'color-token-or-value' },
      typography: { type: 'typography-token-or-value' },
      textAlign: { type: 'enum', options: ['left', 'center', 'right'] },
      maxWidth: { type: 'length' },
    },
    responsiveSchema: { typography: true, textAlign: true, maxWidth: true },
    selectors: { root: '.ps-heading' },
    rendererContract: 'core/heading@1',
  }),

  'core/text': createDefinition({
    type: 'core/text',
    name: 'Text',
    description: 'Paragraph or rich copy block with canonical typography controls.',
    category: COMPONENT_CATEGORIES.TYPOGRAPHY,
    kind: COMPONENT_KINDS.PRIMITIVE,
    defaultProps: { text: 'Add your message here.', semanticTag: 'p' },
    defaultStyle: { typography: 'body-md', color: 'text-secondary' },
    propSchema: {
      text: { type: 'rich-text' },
      semanticTag: { type: 'enum', options: ['p', 'div', 'span'] },
    },
    styleSchema: {
      ...commonSpacingStyle,
      color: { type: 'color-token-or-value' },
      typography: { type: 'typography-token-or-value' },
      textAlign: { type: 'enum', options: ['left', 'center', 'right'] },
      maxWidth: { type: 'length' },
    },
    responsiveSchema: { typography: true, textAlign: true, maxWidth: true },
    selectors: { root: '.ps-text' },
    rendererContract: 'core/text@1',
  }),

  'core/image': createDefinition({
    type: 'core/image',
    name: 'Image',
    description: 'Responsive image with alt text, object fit, and aspect-ratio controls.',
    category: COMPONENT_CATEGORIES.MEDIA,
    kind: COMPONENT_KINDS.PRIMITIVE,
    defaultProps: { src: '', alt: '', loading: 'lazy' },
    defaultStyle: { width: '100%', aspectRatio: 'auto', objectFit: 'cover' },
    propSchema: {
      src: { type: 'asset-image' },
      alt: { type: 'text' },
      loading: { type: 'enum', options: ['lazy', 'eager'] },
      linkUrl: { type: 'url' },
    },
    styleSchema: {
      ...commonSpacingStyle,
      width: { type: 'length' },
      height: { type: 'length' },
      aspectRatio: { type: 'aspect-ratio-token-or-value' },
      objectFit: { type: 'enum', options: ['cover', 'contain', 'fill', 'none'] },
      borderRadius: { type: 'radius-token-or-value' },
    },
    responsiveSchema: { width: true, height: true, aspectRatio: true, objectFit: true },
    selectors: { root: '.ps-image', image: '.ps-image__media' },
    rendererContract: 'core/image@1',
  }),

  'core/video': createDefinition({
    type: 'core/video',
    name: 'Video',
    description: 'Responsive video/embed primitive with poster, autoplay, and aspect controls.',
    category: COMPONENT_CATEGORIES.MEDIA,
    kind: COMPONENT_KINDS.PRIMITIVE,
    defaultProps: { src: '', poster: '', autoplay: false, muted: false, loop: false, controls: true },
    defaultStyle: { width: '100%', aspectRatio: '16/9', objectFit: 'cover' },
    propSchema: {
      src: { type: 'asset-video-or-url' },
      poster: { type: 'asset-image' },
      autoplay: { type: 'boolean' },
      muted: { type: 'boolean' },
      loop: { type: 'boolean' },
      controls: { type: 'boolean' },
    },
    styleSchema: {
      ...commonSpacingStyle,
      width: { type: 'length' },
      height: { type: 'length' },
      aspectRatio: { type: 'aspect-ratio-token-or-value' },
      objectFit: { type: 'enum', options: ['cover', 'contain'] },
      borderRadius: { type: 'radius-token-or-value' },
    },
    responsiveSchema: { width: true, height: true, aspectRatio: true },
    selectors: { root: '.ps-video', media: '.ps-video__media' },
    rendererContract: 'core/video@1',
  }),

  'core/button': createDefinition({
    type: 'core/button',
    name: 'Button',
    description: 'Link/button primitive with canonical visual variants.',
    category: COMPONENT_CATEGORIES.ACTIONS,
    kind: COMPONENT_KINDS.PRIMITIVE,
    defaultProps: { label: 'Button', href: '#', target: '_self', variant: 'primary' },
    defaultStyle: { size: 'md', widthMode: 'auto' },
    propSchema: {
      label: { type: 'text' },
      href: { type: 'url' },
      target: { type: 'enum', options: ['_self', '_blank'] },
      variant: { type: 'button-variant-token' },
      icon: { type: 'icon-reference' },
      iconPosition: { type: 'enum', options: ['start', 'end'] },
    },
    styleSchema: {
      ...commonSpacingStyle,
      size: { type: 'enum', options: ['sm', 'md', 'lg'] },
      widthMode: { type: 'enum', options: ['auto', 'full'] },
      borderRadius: { type: 'radius-token-or-value' },
    },
    responsiveSchema: { size: true, widthMode: true },
    selectors: { root: '.ps-button', icon: '.ps-button__icon', label: '.ps-button__label' },
    rendererContract: 'core/button@1',
  }),

  'core/icon': createDefinition({
    type: 'core/icon',
    name: 'Icon',
    description: 'Accessible icon primitive backed by the PromoShowcase icon registry.',
    category: COMPONENT_CATEGORIES.MEDIA,
    kind: COMPONENT_KINDS.PRIMITIVE,
    defaultProps: { icon: 'sparkles', label: '', decorative: true },
    defaultStyle: { size: '24px', color: 'text-primary' },
    propSchema: {
      icon: { type: 'icon-reference' },
      label: { type: 'text' },
      decorative: { type: 'boolean' },
    },
    styleSchema: {
      ...commonSpacingStyle,
      size: { type: 'length' },
      color: { type: 'color-token-or-value' },
    },
    responsiveSchema: { size: true },
    selectors: { root: '.ps-icon' },
    rendererContract: 'core/icon@1',
  }),

  'core/icon-list': createDefinition({
    type: 'core/icon-list',
    name: 'Icon List',
    description: 'Structured list of icon + text items for features, benefits, and trust points.',
    category: COMPONENT_CATEGORIES.CONTENT,
    kind: COMPONENT_KINDS.COMPOSED,
    defaultProps: {
      items: [
        { icon: 'check', text: 'First benefit' },
        { icon: 'check', text: 'Second benefit' },
      ],
      orientation: 'vertical',
    },
    propSchema: {
      items: { type: 'repeater', fields: { icon: { type: 'icon-reference' }, text: { type: 'text' } } },
      orientation: { type: 'enum', options: ['vertical', 'horizontal'] },
    },
    styleSchema: {
      ...commonSpacingStyle,
      gap: { type: 'spacing-token-or-value' },
      iconColor: { type: 'color-token-or-value' },
      iconSize: { type: 'length' },
      typography: { type: 'typography-token-or-value' },
    },
    responsiveSchema: { orientation: true, gap: true, iconSize: true },
    selectors: { root: '.ps-icon-list', item: '.ps-icon-list__item', icon: '.ps-icon-list__icon' },
    rendererContract: 'core/icon-list@1',
  }),

  'core/spacer': createDefinition({
    type: 'core/spacer',
    name: 'Spacer',
    description: 'Explicit responsive vertical or horizontal spacing primitive.',
    category: COMPONENT_CATEGORIES.LAYOUT,
    kind: COMPONENT_KINDS.PRIMITIVE,
    defaultProps: { axis: 'vertical', size: 'space-8' },
    propSchema: {
      axis: { type: 'enum', options: ['vertical', 'horizontal'] },
      size: { type: 'spacing-token-or-value' },
    },
    responsiveSchema: { axis: true, size: true },
    selectors: { root: '.ps-spacer' },
    rendererContract: 'core/spacer@1',
  }),

  'core/divider': createDefinition({
    type: 'core/divider',
    name: 'Divider',
    description: 'Responsive rule for visual separation between content regions.',
    category: COMPONENT_CATEGORIES.LAYOUT,
    kind: COMPONENT_KINDS.PRIMITIVE,
    defaultProps: { orientation: 'horizontal' },
    defaultStyle: { thickness: '1px', color: 'border-subtle', width: '100%' },
    propSchema: { orientation: { type: 'enum', options: ['horizontal', 'vertical'] } },
    styleSchema: {
      ...commonSpacingStyle,
      thickness: { type: 'length' },
      color: { type: 'color-token-or-value' },
      width: { type: 'length' },
      height: { type: 'length' },
    },
    responsiveSchema: { orientation: true, width: true, height: true },
    selectors: { root: '.ps-divider' },
    rendererContract: 'core/divider@1',
  }),

  'core/image-text': createDefinition({
    type: 'core/image-text',
    name: 'Image + Text',
    description: 'Editorial split layout combining media, copy, and an optional action.',
    category: COMPONENT_CATEGORIES.CONTENT,
    kind: COMPONENT_KINDS.COMPOSED,
    defaultProps: {
      eyebrow: '',
      heading: 'Tell your story',
      body: 'Pair expressive copy with strong imagery.',
      image: '',
      imageAlt: '',
      imagePosition: 'left',
      buttonLabel: '',
      buttonHref: '',
    },
    defaultStyle: { gap: 'space-10', verticalAlign: 'center', imageRatio: '4/3' },
    propSchema: {
      eyebrow: { type: 'text' },
      heading: { type: 'text' },
      body: { type: 'rich-text' },
      image: { type: 'asset-image' },
      imageAlt: { type: 'text' },
      imagePosition: { type: 'enum', options: ['left', 'right'] },
      buttonLabel: { type: 'text' },
      buttonHref: { type: 'url' },
    },
    styleSchema: {
      ...commonSpacingStyle,
      gap: { type: 'spacing-token-or-value' },
      verticalAlign: { type: 'enum', options: ['start', 'center', 'end'] },
      imageRatio: { type: 'aspect-ratio-token-or-value' },
      mediaWidth: { type: 'percentage' },
    },
    responsiveSchema: { imagePosition: true, gap: true, imageRatio: true, mediaWidth: true },
    selectors: {
      root: '.ps-image-text', media: '.ps-image-text__media', content: '.ps-image-text__content',
      eyebrow: '.ps-image-text__eyebrow', heading: '.ps-image-text__heading', body: '.ps-image-text__body',
    },
    rendererContract: 'core/image-text@1',
  }),

  'core/card': createDefinition({
    type: 'core/card',
    name: 'Card',
    description: 'Flexible content card for promotions, features, collections, and navigation.',
    category: COMPONENT_CATEGORIES.CONTENT,
    kind: COMPONENT_KINDS.COMPOSED,
    acceptsChildren: true,
    defaultProps: { href: '', variant: 'surface', mediaPlacement: 'top' },
    defaultStyle: { padding: 'space-6', borderRadius: 'radius-lg', background: 'surface-primary' },
    propSchema: {
      href: { type: 'url' },
      variant: { type: 'enum', options: ['surface', 'outline', 'media-overlay', 'minimal'] },
      mediaPlacement: { type: 'enum', options: ['top', 'left', 'right', 'background', 'none'] },
    },
    styleSchema: {
      ...commonSpacingStyle,
      ...commonSurfaceStyle,
      gap: { type: 'spacing-token-or-value' },
    },
    responsiveSchema: { mediaPlacement: true, paddingBlock: true, paddingInline: true, gap: true },
    selectors: { root: '.ps-card', media: '.ps-card__media', content: '.ps-card__content' },
    rendererContract: 'core/card@1',
  }),

  'core/cta': createDefinition({
    type: 'core/cta',
    name: 'CTA',
    description: 'High-intent call-to-action block with polished text and button composition.',
    category: COMPONENT_CATEGORIES.ACTIONS,
    kind: COMPONENT_KINDS.COMPOSED,
    defaultProps: {
      eyebrow: '',
      heading: 'Ready to get started?',
      body: 'Give visitors one clear next step.',
      primaryLabel: 'Get started',
      primaryHref: '#',
      secondaryLabel: '',
      secondaryHref: '',
      alignment: 'left',
    },
    defaultStyle: { paddingBlock: 'section-md', paddingInline: 'space-8', borderRadius: 'radius-xl' },
    propSchema: {
      eyebrow: { type: 'text' }, heading: { type: 'text' }, body: { type: 'rich-text' },
      primaryLabel: { type: 'text' }, primaryHref: { type: 'url' },
      secondaryLabel: { type: 'text' }, secondaryHref: { type: 'url' },
      alignment: { type: 'enum', options: ['left', 'center', 'right'] },
    },
    styleSchema: { ...commonSpacingStyle, ...commonSurfaceStyle, maxWidth: { type: 'length' } },
    responsiveSchema: { alignment: true, paddingBlock: true, paddingInline: true, maxWidth: true },
    selectors: { root: '.ps-cta', heading: '.ps-cta__heading', actions: '.ps-cta__actions' },
    rendererContract: 'core/cta@1',
  }),

  'core/testimonial': createDefinition({
    type: 'core/testimonial',
    name: 'Testimonial',
    description: 'Customer quote with author, role/company, avatar, and rating support.',
    category: COMPONENT_CATEGORIES.CONTENT,
    kind: COMPONENT_KINDS.COMPOSED,
    defaultProps: {
      quote: 'Add a memorable customer quote.', author: 'Customer name', role: '', company: '', avatar: '', rating: 5,
    },
    defaultStyle: { alignment: 'left' },
    propSchema: {
      quote: { type: 'rich-text' }, author: { type: 'text' }, role: { type: 'text' }, company: { type: 'text' },
      avatar: { type: 'asset-image' }, rating: { type: 'number', min: 0, max: 5 },
    },
    styleSchema: { ...commonSpacingStyle, ...commonSurfaceStyle, alignment: { type: 'enum', options: ['left', 'center'] } },
    responsiveSchema: { alignment: true },
    selectors: { root: '.ps-testimonial', quote: '.ps-testimonial__quote', author: '.ps-testimonial__author' },
    rendererContract: 'core/testimonial@1',
  }),

  'core/accordion': createDefinition({
    type: 'core/accordion',
    name: 'Accordion',
    description: 'Disclosure list for dense supporting information.',
    category: COMPONENT_CATEGORIES.CONTENT,
    kind: COMPONENT_KINDS.COMPOSED,
    defaultProps: {
      items: [{ title: 'Accordion item', content: 'Add supporting content.' }], allowMultiple: false, firstOpen: false,
    },
    propSchema: {
      items: { type: 'repeater', fields: { title: { type: 'text' }, content: { type: 'rich-text' } } },
      allowMultiple: { type: 'boolean' }, firstOpen: { type: 'boolean' },
    },
    styleSchema: { ...commonSpacingStyle, dividerColor: { type: 'color-token-or-value' }, gap: { type: 'spacing-token-or-value' } },
    responsiveSchema: { gap: true },
    selectors: { root: '.ps-accordion', item: '.ps-accordion__item', trigger: '.ps-accordion__trigger', panel: '.ps-accordion__panel' },
    rendererContract: 'core/accordion@1',
  }),

  'core/tabs': createDefinition({
    type: 'core/tabs',
    name: 'Tabs',
    description: 'Tabbed content groups with accessible tab/panel semantics.',
    category: COMPONENT_CATEGORIES.CONTENT,
    kind: COMPONENT_KINDS.COMPOSED,
    defaultProps: {
      items: [{ label: 'Tab one', content: 'Tab content' }, { label: 'Tab two', content: 'More content' }],
      orientation: 'horizontal', activeIndex: 0,
    },
    propSchema: {
      items: { type: 'repeater', fields: { label: { type: 'text' }, content: { type: 'rich-text' } } },
      orientation: { type: 'enum', options: ['horizontal', 'vertical'] }, activeIndex: { type: 'number', min: 0 },
    },
    styleSchema: { ...commonSpacingStyle, gap: { type: 'spacing-token-or-value' }, activeColor: { type: 'color-token-or-value' } },
    responsiveSchema: { orientation: true, gap: true },
    selectors: { root: '.ps-tabs', list: '.ps-tabs__list', tab: '.ps-tabs__tab', panel: '.ps-tabs__panel' },
    rendererContract: 'core/tabs@1',
  }),

  'core/faq': createDefinition({
    type: 'core/faq',
    name: 'FAQ',
    description: 'FAQ composition with heading, introduction, and question/answer items.',
    category: COMPONENT_CATEGORIES.CONTENT,
    kind: COMPONENT_KINDS.COMPOSED,
    defaultProps: {
      eyebrow: 'FAQ', heading: 'Frequently asked questions', intro: '',
      items: [{ question: 'What should customers know?', answer: 'Add the answer here.' }],
    },
    propSchema: {
      eyebrow: { type: 'text' }, heading: { type: 'text' }, intro: { type: 'rich-text' },
      items: { type: 'repeater', fields: { question: { type: 'text' }, answer: { type: 'rich-text' } } },
    },
    styleSchema: { ...commonSpacingStyle, maxWidth: { type: 'length' }, layout: { type: 'enum', options: ['stacked', 'split'] } },
    responsiveSchema: { maxWidth: true, layout: true },
    selectors: { root: '.ps-faq', header: '.ps-faq__header', item: '.ps-faq__item' },
    rendererContract: 'core/faq@1',
  }),

  'core/stats': createDefinition({
    type: 'core/stats',
    name: 'Stats',
    description: 'High-impact metric grid for proof points, milestones, and outcomes.',
    category: COMPONENT_CATEGORIES.CONTENT,
    kind: COMPONENT_KINDS.COMPOSED,
    defaultProps: {
      items: [
        { value: '10K+', label: 'Customers served', detail: '' },
        { value: '98%', label: 'Satisfaction', detail: '' },
        { value: '24h', label: 'Typical response', detail: '' },
      ],
      columns: 3,
    },
    propSchema: {
      items: { type: 'repeater', fields: { value: { type: 'text' }, label: { type: 'text' }, detail: { type: 'text' } } },
      columns: { type: 'number', min: 1, max: 6 },
    },
    styleSchema: { ...commonSpacingStyle, gap: { type: 'spacing-token-or-value' }, alignment: { type: 'enum', options: ['left', 'center'] } },
    responsiveSchema: { columns: true, gap: true, alignment: true },
    selectors: { root: '.ps-stats', item: '.ps-stats__item', value: '.ps-stats__value', label: '.ps-stats__label' },
    rendererContract: 'core/stats@1',
  }),
});

export const getComponentDefinition = (type) => CORE_COMPONENT_REGISTRY[type] || null;

export const isRegisteredComponent = (type) => Boolean(getComponentDefinition(type));

export const listComponentDefinitions = ({ category, kind } = {}) =>
  Object.values(CORE_COMPONENT_REGISTRY).filter((definition) => {
    if (category && definition.category !== category) return false;
    if (kind && definition.kind !== kind) return false;
    return true;
  });

export const canComponentAcceptChildren = (type) => Boolean(getComponentDefinition(type)?.acceptsChildren);

export const getRendererContract = (type) => getComponentDefinition(type)?.rendererContract || null;

export const isCustomComponentType = (type) => typeof type === 'string' && type.startsWith('custom/');
